import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import { IcoArrow, IcoUsers } from '../assets/icons';

const partnersFull = [
  { abbr: 'GovAO', name: 'Governo da República de Angola',    type_pt: 'Governamental', type_en: 'Governmental', since: '2020', desc_pt: 'Parceria institucional com o Governo angolano para o desenvolvimento das comunidades vulneráveis do país.', desc_en: 'Institutional partnership with the Angolan Government for the development of the country\'s vulnerable communities.' },
  { abbr: 'MINSA', name: 'Ministério da Saúde de Angola',     type_pt: 'Governamental', type_en: 'Governmental', since: '2020', desc_pt: 'Parceria para programas de saúde comunitária, rastreios médicos e campanhas de vacinação.', desc_en: 'Partnership for community health programmes, medical screenings and vaccination campaigns.' },
  { abbr: 'MED',   name: 'Ministério da Educação de Angola',  type_pt: 'Governamental', type_en: 'Governmental', since: '2020', desc_pt: 'Colaboração em programas educativos e construção de infraestruturas escolares.', desc_en: 'Collaboration on educational programmes and construction of school infrastructure.' },
  { abbr: 'UNICEF',name: 'UNICEF Angola',                     type_pt: 'Internacional', type_en: 'International', since: '2021', desc_pt: 'Parceria estratégica em programas de protecção infantil, nutrição e saúde materno-infantil.', desc_en: 'Strategic partnership in child protection, nutrition and maternal-child health programmes.' },
  { abbr: 'UNFPA', name: 'UNFPA Angola',                      type_pt: 'Internacional', type_en: 'International', since: '2021', desc_pt: 'Cooperação em saúde reprodutiva, empoderamento feminino e protecção de mulheres vulneráveis.', desc_en: 'Cooperation on reproductive health, female empowerment and protection of vulnerable women.' },
  { abbr: 'WHO',   name: 'OMS — Organização Mundial de Saúde',type_pt: 'Internacional', type_en: 'International', since: '2021', desc_pt: 'Alinhamento com os padrões internacionais de saúde pública nos programas da Fundação.', desc_en: 'Alignment with international public health standards in the Foundation\'s programmes.' },
  { abbr: 'EU',    name: 'União Europeia',                    type_pt: 'Internacional', type_en: 'International', since: '2022', desc_pt: 'Financiamento e assistência técnica para projectos de desenvolvimento comunitário e rural.', desc_en: 'Funding and technical assistance for community and rural development projects.' },
  { abbr: 'ICRC',  name: 'Cruz Vermelha Angolana',            type_pt: 'Nacional',      type_en: 'National',      since: '2020', desc_pt: 'Coordenação em resposta humanitária a crises, emergências e assistência directa às famílias.', desc_en: 'Coordination on humanitarian response to crises, emergencies and direct family assistance.' },
];

const typeStyle = {
  'Governamental': 'bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 border-brand-200 dark:border-brand-800',
  'Governmental':  'bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 border-brand-200 dark:border-brand-800',
  'Internacional': 'bg-brand-600/10 dark:bg-brand-600/10 text-brand-700 dark:text-brand-300 border-brand-300 dark:border-brand-700',
  'International': 'bg-brand-600/10 dark:bg-brand-600/10 text-brand-700 dark:text-brand-300 border-brand-300 dark:border-brand-700',
  'Nacional':      'bg-white dark:bg-brand-950 text-brand-600 dark:text-brand-500 border-brand-200 dark:border-brand-800',
  'National':      'bg-white dark:bg-brand-950 text-brand-600 dark:text-brand-500 border-brand-200 dark:border-brand-800',
};

export default function PartnersPage({ onNavigate }) {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  const ref1 = useAppear();
  const ref2 = useAppear();

  return (
    <main>
      {/* Hero */}
      <section className="relative bg-brand-950 pt-[68px] overflow-hidden">
        <div className="absolute right-0 top-0 w-[600px] h-[600px] rounded-full bg-brand-600/8 blur-[120px] translate-x-1/2 -translate-y-1/3" />
        <div className="wrap relative z-10 py-24 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-800 bg-brand-900/60 mb-7">
            <span className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.14em]">
              {isPt ? 'Parceiros Estratégicos' : 'Strategic Partners'}
            </span>
          </div>
          <h1 className="display-xl text-white mb-6">
            {isPt ? 'Os Nossos Parceiros' : 'Our Partners'}
          </h1>
          <p className="body-lg text-brand-400 max-w-xl mx-auto">
            {isPt
              ? 'Juntos chegamos mais longe. A nossa rede de parceiros nacionais e internacionais multiplica o impacto da Fundação.'
              : 'Together we reach further. Our network of national and international partners multiplies the Foundation\'s impact.'}
          </p>
        </div>
      </section>

      {/* Partners grid */}
      <section id="estrategicos" className="section-pad bg-white dark:bg-brand-950">
        <div className="wrap">
          <div ref={ref1} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-20">
            {partnersFull.map((p, i) => {
              const typeKey = isPt ? p.type_pt : p.type_en;
              return (
                <div
                  key={i}
                  className="card-hover p-7 group"
                >
                  {/* Top */}
                  <div className="flex items-start justify-between mb-5">
                    {/* Logo placeholder */}
                    <div className="w-14 h-14 rounded-xl bg-brand-50 dark:bg-brand-900 border border-brand-100 dark:border-brand-800 flex items-center justify-center">
                      <span className="text-[11px] font-black text-brand-600 dark:text-brand-500 tracking-tight">
                        {p.abbr}
                      </span>
                    </div>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border uppercase tracking-wider ${typeStyle[typeKey] || typeStyle['Governamental']}`}>
                      {typeKey}
                    </span>
                  </div>

                  {/* Name */}
                  <div className="h-px w-8 bg-brand-600 mb-4 group-hover:w-full transition-all duration-300" />
                  <h4 className="font-bold text-brand-900 dark:text-white text-[14px] leading-snug mb-1">
                    {p.name}
                  </h4>
                  <p className="text-[11px] text-brand-500 mb-3">
                    {isPt ? 'Parceiro desde' : 'Partner since'} {p.since}
                  </p>

                  {/* Description */}
                  <p className="text-sm text-brand-600 dark:text-brand-500 leading-relaxed">
                    {isPt ? p.desc_pt : p.desc_en}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Become a partner CTA */}
          <div
            ref={ref2}
            id="candidatura"
            className="card-orange p-12 lg:p-16 text-center relative overflow-hidden"
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-[0.07]">
              <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
                <defs>
                  <pattern id="pp" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
                    <path d="M24 4 L44 44 L4 44 Z" fill="none" stroke="white" strokeWidth="0.8"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#pp)"/>
              </svg>
            </div>
            <div className="absolute right-0 top-0 w-80 h-80 rounded-full bg-brand-700/30 blur-[80px] translate-x-1/2 -translate-y-1/2" />

            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
                <IcoUsers cls="w-7 h-7 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {isPt ? 'Torne-se Parceiro' : 'Become a Partner'}
              </h3>
              <p className="text-brand-100 max-w-lg mx-auto mb-8 leading-relaxed">
                {isPt
                  ? 'A sua organização, empresa ou instituição pode fazer parte desta missão de transformação social em Angola.'
                  : 'Your organisation, company or institution can be part of this social transformation mission in Angola.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => onNavigate && onNavigate('/contacto')}
                  className="btn-white"
                >
                  {isPt ? 'Contactar a Fundação' : 'Contact the Foundation'}
                  <IcoArrow cls="w-4 h-4" />
                </button>
                <a
                  href="mailto:parcerias@nganazenza.org"
                  className="btn-ghost-white"
                >
                  geral@fdc.ao
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
